import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';

/** A field still to do: its stored value was rejected, or it is awaiting review and filled. */
const isFieldToDo = (field: DocumentFieldView): boolean =>
	field.isRejectedValue || (field.isPendingReview && !field.isEmpty);

/**
 * The next or previous field to do, in the order of `orderedFields`, wrapping around. A field the
 * document shows nowhere is skipped (`isShown`): its passage is hidden. Starts from `currentKey`, or
 * from the top (`next`) / bottom (`previous`) when it is absent or unknown. `null` when nothing is to do.
 */
const findAdjacentPendingField = (
	orderedFields: readonly DocumentFieldView[],
	currentKey: string | null,
	direction: 'next' | 'previous',
	isShown: (field: DocumentFieldView) => boolean,
): DocumentFieldView | null => {
	const candidates = orderedFields.filter((field) => isFieldToDo(field) && isShown(field));

	if (candidates.length === 0) {
		return null;
	}

	const currentOrder = orderedFields.findIndex((field) => field.key === currentKey);

	if (direction === 'next') {
		return (
			candidates.find((field) => orderedFields.indexOf(field) > currentOrder) ?? candidates[0] ?? null
		);
	}

	const previous = candidates.findLast(
		(field) => currentOrder === -1 || orderedFields.indexOf(field) < currentOrder,
	);

	return previous ?? candidates.at(-1) ?? null;
};

export default findAdjacentPendingField;
